/*
 * Prometheus TEXT exposition parser + a polling hook for the Mesh Console.
 *
 * /v1/metrics is the ONLY live time-series source for Transport (there is no
 * "list dispatches" endpoint). We parse the TEXT format honestly: counters,
 * gauges, and histogram buckets with their {le=...} labels.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getMetricsText, MeshApiError } from "./client";
import type { PlaneHealth } from "./client";
import type { MetricSample, MetricPoint } from "./types";

/* ---------- Parser ---------- */

/**
 * Parse Prometheus TEXT exposition into flat samples.
 * Handles: comments (# HELP / # TYPE skipped), labelled & unlabelled series,
 * the optional trailing timestamp, NaN/+Inf/-Inf values, and escaped label
 * values (\\, \", \n).
 */
export function parsePrometheusText(text: string): MetricSample[] {
  const out: MetricSample[] = [];
  const lines = text.split("\n");

  for (const raw of lines) {
    const line = raw.trim();
    if (line.length === 0 || line.startsWith("#")) continue;

    // metric_name{label="v",...} value [timestamp]
    // or         metric_name value [timestamp]
    const braceStart = line.indexOf("{");
    let name: string;
    let labels: Record<string, string> = {};
    let rest: string;

    if (braceStart !== -1) {
      name = line.slice(0, braceStart).trim();
      const braceEnd = line.indexOf("}", braceStart);
      if (braceEnd === -1) continue; // malformed
      labels = parseLabels(line.slice(braceStart + 1, braceEnd));
      rest = line.slice(braceEnd + 1).trim();
    } else {
      const sp = line.indexOf(" ");
      if (sp === -1) continue;
      name = line.slice(0, sp).trim();
      rest = line.slice(sp + 1).trim();
    }

    const valueToken = rest.split(/\s+/)[0];
    const value = parseValue(valueToken);
    if (name.length === 0) continue;
    out.push({ name, labels, value });
  }
  return out;
}

function parseLabels(inner: string): Record<string, string> {
  const labels: Record<string, string> = {};
  // Match key="value" pairs, tolerating commas inside quoted values.
  const re = /([a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*"((?:\\.|[^"\\])*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(inner)) !== null) {
    labels[m[1]] = unescapeLabel(m[2]);
  }
  return labels;
}

function unescapeLabel(v: string): string {
  return v.replace(/\\(["\\n])/g, (_, c: string) =>
    c === "n" ? "\n" : c,
  );
}

function parseValue(token: string | undefined): number {
  if (token === undefined) return NaN;
  switch (token) {
    case "+Inf":
      return Number.POSITIVE_INFINITY;
    case "-Inf":
      return Number.NEGATIVE_INFINITY;
    case "NaN":
      return NaN;
    default:
      return Number(token);
  }
}

/* ---------- Selection helpers ---------- */

/** Sum every sample of a metric family (ignoring labels). Counters/gauges. */
export function sumMetric(samples: MetricSample[], name: string): number {
  return samples
    .filter((s) => s.name === name && Number.isFinite(s.value))
    .reduce((acc, s) => acc + s.value, 0);
}

/** First sample of a metric matching an optional label filter. */
export function findMetric(
  samples: MetricSample[],
  name: string,
  labels?: Record<string, string>,
): MetricSample | undefined {
  return samples.find(
    (s) =>
      s.name === name &&
      (!labels || Object.entries(labels).every(([k, v]) => s.labels[k] === v)),
  );
}

/** All samples for one metric family. */
export function selectFamily(samples: MetricSample[], name: string): MetricSample[] {
  return samples.filter((s) => s.name === name);
}

/**
 * Approximate a quantile from histogram buckets (…_bucket with le labels) and
 * the …_count total. Returns seconds, or undefined if buckets are absent.
 * Honest about being an estimate (linear interpolation within the bucket).
 */
export function histogramQuantile(
  samples: MetricSample[],
  bucketName: string,
  countName: string,
  q: number,
): number | undefined {
  const buckets = selectFamily(samples, bucketName)
    .map((s) => ({ le: Number(s.labels.le), count: s.value }))
    .filter((b) => Number.isFinite(b.le) || b.le === Infinity)
    .sort((a, b) => a.le - b.le);
  if (buckets.length === 0) return undefined;
  const total =
    findMetric(samples, countName)?.value ?? buckets[buckets.length - 1].count;
  if (!Number.isFinite(total) || total <= 0) return undefined;

  const target = q * total;
  let prevLe = 0;
  let prevCount = 0;
  for (const b of buckets) {
    if (b.count >= target) {
      if (b.le === Infinity) return prevLe;
      const span = b.count - prevCount;
      if (span <= 0) return b.le;
      const frac = (target - prevCount) / span;
      return prevLe + frac * (b.le - prevLe);
    }
    prevLe = b.le === Infinity ? prevLe : b.le;
    prevCount = b.count;
  }
  return prevLe;
}

/* ---------- Poller hook ---------- */

export interface MetricsPoll {
  /** Latest parsed samples, or null before the first successful poll. */
  samples: MetricSample[] | null;
  /** Error from the most recent poll attempt, if it failed. */
  error: MeshApiError | null;
  /** True while a poll is in flight. */
  loading: boolean;
  /** Timestamp (ms) of the last successful parse. */
  lastUpdated: number | null;
  /** Force an immediate refetch. */
  refresh: () => void;
}

/**
 * Poll /v1/metrics on an interval and keep the latest parsed samples.
 * Pass intervalMs = 0 to fetch once and stop. Honours unmount cleanly.
 */
export function useMetricsPoll(intervalMs = 5000): MetricsPoll {
  const [samples, setSamples] = useState<MetricSample[] | null>(null);
  const [error, setError] = useState<MeshApiError | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const aliveRef = useRef(true);
  const tickRef = useRef(0);

  const poll = useCallback(async () => {
    setLoading(true);
    try {
      const text = await getMetricsText();
      if (!aliveRef.current) return;
      setSamples(parsePrometheusText(text));
      setError(null);
      setLastUpdated(Date.now());
    } catch (e) {
      if (!aliveRef.current) return;
      setError(
        e instanceof MeshApiError ? e : new MeshApiError("poll failed", 0, "/v1/metrics"),
      );
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    tickRef.current += 1;
    void poll();
  }, [poll]);

  useEffect(() => {
    aliveRef.current = true;
    void poll();
    if (intervalMs <= 0) return () => void (aliveRef.current = false);
    const id = setInterval(() => void poll(), intervalMs);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, [poll, intervalMs]);

  return { samples, error, loading, lastUpdated, refresh };
}

/**
 * Maintain a rolling client-side time series for a single scalar derived from
 * each poll. Caller supplies a selector over the latest samples. Keeps the
 * most recent `cap` points. Metrics are poll-derived, so this is how the
 * Transport view builds its live charts.
 */
export function useDerivedSeries(
  samples: MetricSample[] | null,
  select: (s: MetricSample[]) => number,
  cap = 60,
): MetricPoint[] {
  const [series, setSeries] = useState<MetricPoint[]>([]);
  const selectRef = useRef(select);
  selectRef.current = select;

  useEffect(() => {
    if (!samples) return;
    const value = selectRef.current(samples);
    if (!Number.isFinite(value)) return;
    setSeries((prev) => {
      const next = [...prev, { t: Date.now(), value }];
      return next.length > cap ? next.slice(next.length - cap) : next;
    });
  }, [samples, cap]);

  return series;
}

/* ---------- Plane health ---------- */

/** A plane's live health-probe status, as a view consumes it. */
export type PlaneHealthState =
  | { kind: "probing" }
  | { kind: "up"; latencyMs: number }
  | { kind: "down" };

/**
 * Probe one plane's deployed health stub ONCE, abortably (same posture as the
 * metrics poller). `probe` is a plane's namespaced `health()` fn (e.g.
 * `trust.health`), which resolves {up, latencyMs} truthfully and never throws.
 *
 * The dedicated plane views thread the result into PlaneState's
 * `serviceHealthy`/`serviceLatencyMs` props so an offline read-API surface
 * renders "deployed · health 200 · API pending" when the service is up, and
 * the honest "offline — awaiting service" only when it is genuinely down.
 *
 * Honesty: `up` is only ever true for a REAL successful probe. A probe that is
 * still in flight is `probing` (treated as not-yet-healthy by callers), never
 * assume-healthy.
 */
export function usePlaneHealth(
  probe: (opts?: { signal?: AbortSignal }) => Promise<PlaneHealth>,
): { state: PlaneHealthState; recheck: () => void } {
  const [state, setState] = useState<PlaneHealthState>({ kind: "probing" });
  const probeRef = useRef(probe);
  probeRef.current = probe;

  const recheck = useCallback(() => {
    const ctrl = new AbortController();
    setState({ kind: "probing" });
    probeRef.current({ signal: ctrl.signal })
      .then((h) => setState(h.up ? { kind: "up", latencyMs: h.latencyMs } : { kind: "down" }))
      .catch(() => setState({ kind: "down" }));
    return ctrl;
  }, []);

  useEffect(() => {
    const ctrl = recheck();
    return () => ctrl.abort();
  }, [recheck]);

  return { state, recheck };
}
